import { AbstractControl, ValidationErrors } from '@angular/forms';

interface ManualError<E> {
  /** Appends the specific error to the given control. */
  append: <T extends AbstractControl>(control: T, error: E) => void;
  /** Removes the specific error code from the given control. */
  remove: <T extends AbstractControl>(control: T) => void;
}

/**
 * Creates utility functions to manually append / remove an error code to a control.
 *
 * @param errorCode The error code to append / remove
 * @param opts Default options, see {@link AbstractControl.setErrors}
 * @returns
 */
export function makeManual<E>(
  errorCode: string,
  opts?: {
    emitEvent?: boolean;
  },
): ManualError<E> {
  const _getRemainingErrors = _omitError(errorCode);

  const append = <T extends AbstractControl>(control: T, error: E) => {
    control.setErrors(
      { ..._getRemainingErrors(control.errors ?? {}), [errorCode]: error },
      opts,
    );
  };
  const remove = <T extends AbstractControl>(control: T) => {
    if (control.hasError(errorCode)) {
      control.setErrors(_getRemainingErrors(control.errors ?? {}), opts);
    }
  };

  return { remove, append };
}

function _omitError(errorCode: string) {
  return (errors: ValidationErrors): ValidationErrors | null => {
    const { [errorCode]: _, ...others } = errors;
    return _coalesceEmptyError(others);
  };
}

function _coalesceEmptyError(e: ValidationErrors): ValidationErrors | null {
  return Object.keys(e).length === 0 ? null : e;
}
